/** @format **/
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import PaperedTable from '../../../components/Table/PaperedTable';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import TableCell from '@material-ui/core/TableCell';
import { TableBody, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import FilterList from '../../../components/Filter/FilterList';
import { FilterReducerFieldName, FilterReducers } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { FilterManager } from '../../../modules/filter/FilterManager';
import { BlockActions } from '../../../modules/block/blockReducers';
import { useFilterParseToArray } from '../../../modules/lib/hooks/useFilterParseToArray';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import Pager from '../../../components/Adescom/PagerRange';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { MvnoBlockErrorListInterface } from '../../../interfaces/platform/MvnoBlockErrorListInterface';
import { ApiCollectionInterface } from '../../../interfaces/collections/ApiCollectionInterface';
import FabAddButton from '../../../components/Buttons/FabAddButton';
import { MvnoErrorManagementDialog } from './MvnoErrorManagementDialog';
import OPL_STYLE from '../../../assets/themes/OPL/OplStyle';
import I18n from '../../../components/Translate/I18n';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import EditButton from '../../../components/Buttons/EditButton';
import BlockButton from '../../../components/Buttons/BlockButton';

type MvnoBlockErrorManagementType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [];
const setDefaultOrders = (): Array<Order> => [{ name: 'id', value: 'asc' }];

const actionTypes: Array<HeaderCheckBoxSelectListElement> = [
    { value: 1, name: 'Service SDG' },
    { value: 2, name: 'Subscribe SDG' },
];

const MvnoBlockErrorManagement: FunctionComponent<MvnoBlockErrorManagementType> = ({ classes, showMainLoader }): React.ReactElement => {
    const dispatch = useDispatch();
    const filterStack = useRef<Array<Filter>>(setDefaultFilters());
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [blockList, setBlockList] = useState<ApiCollectionInterface<MvnoBlockErrorListInterface> | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(true);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [editError, setEditError] = useState<MvnoBlockErrorListInterface | null>(null);

    filterStack.current = useFilterParseToArray(filters, false);

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_error_lists')),
                BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
                filterStack.current,
                orders,
                (data: ApiCollectionInterface<MvnoBlockErrorListInterface>) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    setBlockList(data);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filterStack, orders, reload, showMainLoader]);

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
            (data: ApiCollectionInterface<MvnoBlockErrorListInterface>): void => {
                setBlockList(data);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const addNewError = (): void => {
        setShowDialog(true);
    };

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        setReload(true);
    };
    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const lockCallback = (iri: { object: MvnoBlockErrorListInterface; lockFlag: boolean }, event: any): void => {
        let itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(iri.object['@id']),
            'mvno_error_management_post',
            { active: iri.lockFlag, actionType: iri.object.actionType },
            (data: any): void => {
                setReload(true);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader}>
                                <TableHeaderContentCell
                                    title="prov:error_management.error_content"
                                    fieldName="errorMessage"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:error_management.action_type"
                                    textAlign="left"
                                    fieldName="activeType"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={actionTypes}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:error_management.active"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="active"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {blockList &&
                            blockList['hydra:member'].map((item) => {
                                let rowActiveColor = 'white';
                                if (activeRow === item.id) {
                                    rowActiveColor = OPL_STYLE.TABLE.ACTIVE_ROW;
                                }
                                return (
                                    <TableRow
                                        classes={tableRowRoot}
                                        key={'key' + item.id}
                                        hover
                                        onClick={(): void => {
                                            setActiveRow(item.id);
                                        }}
                                        style={{ backgroundColor: rowActiveColor }}
                                    >
                                        <TableCell
                                            classes={tableControlStickyHeader}
                                            padding="none"
                                            align="left"
                                            style={{ backgroundColor: rowActiveColor }}
                                        >
                                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_BLOCK_CONTAINS_GET) && (
                                                <EditButton
                                                    callback={(object: MvnoBlockErrorListInterface): void => {
                                                        setEditError(object);
                                                        setShowDialog(true);
                                                    }}
                                                    callbackParam={{ ...item }}
                                                    tooltipTitle="general:common.edit"
                                                    tooltipPlacement="bottom-start"
                                                />
                                            )}
                                            {item.active && (
                                                <BlockButton
                                                    callback={lockCallback}
                                                    callbackParam={{ object: item, lockFlag: false }}
                                                    tooltipTitle="prov:error_management.lock"
                                                    tooltipPlacement="bottom-start"
                                                />
                                            )}
                                            {!item.active && (
                                                <BlockButton
                                                    callback={lockCallback}
                                                    callbackParam={{ object: item, lockFlag: true }}
                                                    tooltipTitle="prov:error_management.unlock"
                                                    tooltipPlacement="bottom-start"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {item.errorMessage}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {item.actionType === 1 && 'Service SDG'}
                                            {item.actionType === 2 && 'Subscribe SDG'}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                            {<I18n trans={item.active ? 'general:common.yes' : 'general:common.no'} />}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {blockList && blockList['hydra:view'] && <Pager callback={paginationCallbackFunction} views={blockList} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
                <FabAddButton callback={addNewError} />
                <MvnoErrorManagementDialog
                    closeCallback={(): void => {
                        setShowDialog(false);
                        setEditError(null);
                    }}
                    reloadCallback={(): void => {
                        setReload(true);
                        setEditError(null);
                    }}
                    open={showDialog}
                    editMessage={editError}
                    actionTypes={actionTypes}
                />
            </GridItem>
        </GridContainer>
    );
};
export default withStyles(oplAppStyle)(MvnoBlockErrorManagement);
